import { z } from 'zod';
export const validators = {
    email: z.string().email({ message: 'Please enter a valid email address' }),
    phone: z.string().regex(/^\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})$/, { message: 'Please enter a valid phone number (e.g., 555-123-4567)' }),
    zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, { message: 'Please enter a valid ZIP code (e.g., 78701 or 78701-1234)' }),
    ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, { message: 'Please enter a valid SSN (e.g., 123-45-6789)' }),
    uuid: z.string().uuid({ message: 'Invalid ID format' }),
    date: z.string().datetime({ message: 'Please enter a valid date' }),
    requiredString: (fieldName, minLength = 1) => z.string().min(minLength, `${fieldName} is required`),
    optionalString: z.string().optional(),
    positiveNumber: (fieldName) => z.number().positive(`${fieldName} must be a positive number`),
    password: z.string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/\d/, 'Password must contain at least one number'),
    confirmPassword: (passwordField) => z.string().refine((val) => val === passwordField, {
        message: 'Passwords do not match'
    })
};
export const clientSchema = z.object({
    first_name: validators.requiredString('First name', 2),
    last_name: validators.requiredString('Last name', 2),
    email: validators.email.optional(),
    phone: validators.phone,
    date_of_birth: validators.date,
    ssn: validators.ssn.optional(),
    address: z.object({
        street: validators.requiredString('Street address'),
        city: validators.requiredString('City'),
        state: z.enum(['TX', 'FL', 'OH', 'PA', 'GA', 'NC', 'AZ']),
        zip: validators.zipCode
    }),
    emergency_contact: z.object({
        name: validators.requiredString('Emergency contact name'),
        phone: validators.phone,
        relationship: validators.requiredString('Relationship')
    }),
    notes: validators.optionalString
});
export const caregiverSchema = z.object({
    first_name: validators.requiredString('First name', 2),
    last_name: validators.requiredString('Last name', 2),
    email: validators.email,
    phone: validators.phone,
    date_of_birth: validators.date,
    ssn: validators.ssn,
    certifications: z.array(z.string()).min(1, 'At least one certification required'),
    hourly_rate: validators.positiveNumber('Hourly rate'),
    address: z.object({
        street: validators.requiredString('Street address'),
        city: validators.requiredString('City'),
        state: z.enum(['TX', 'FL', 'OH', 'PA', 'GA', 'NC', 'AZ']),
        zip: validators.zipCode
    })
});
export const visitSchema = z.object({
    client_id: validators.uuid,
    caregiver_id: validators.uuid,
    scheduled_start: validators.date,
    scheduled_end: validators.date,
    service_type: z.enum(['personal_care', 'companionship', 'skilled_nursing', 'respite']),
    notes: validators.optionalString
}).refine((data) => new Date(data.scheduled_end) > new Date(data.scheduled_start), {
    message: 'End time must be after start time',
    path: ['scheduled_end']
});
export const carePlanSchema = z.object({
    client_id: validators.uuid,
    name: validators.requiredString('Plan name', 3),
    start_date: validators.date,
    end_date: validators.date.optional(),
    goals: validators.requiredString('Care goals', 10),
    status: z.enum(['draft', 'active', 'completed', 'cancelled'])
});
export const loginSchema = z.object({
    email: validators.email,
    password: z.string().min(1, 'Password is required')
});
export const registerSchema = z.object({
    email: validators.email,
    password: validators.password,
    confirm_password: z.string(),
    first_name: validators.requiredString('First name'),
    last_name: validators.requiredString('Last name'),
    phone: validators.phone
}).refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password']
});
export const validationHelpers = {
    isEmail: (email) => {
        const emailRegex = /^[\w%+.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,}$/;
        return emailRegex.test(email);
    },
    isPhone: (phone) => {
        const phoneRegex = /^\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})$/;
        return phoneRegex.test(phone);
    },
    isZipCode: (zip) => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(zip);
    },
    isSSN: (ssn) => {
        const ssnRegex = /^\d{3}-?\d{2}-?\d{4}$/;
        return ssnRegex.test(ssn);
    },
    isEmpty: (value) => {
        if (value == null)
            return true;
        if (typeof value === 'string')
            return value.trim() === '';
        if (Array.isArray(value))
            return value.length === 0;
        if (typeof value === 'object')
            return Object.keys(value).length === 0;
        return false;
    },
    isValidDate: (date) => {
        const d = typeof date === 'string' ? new Date(date) : date;
        return d instanceof Date && !isNaN(d.getTime());
    },
    minLength: (value, min) => {
        return value.length >= min;
    },
    maxLength: (value, max) => {
        return value.length <= max;
    },
};
//# sourceMappingURL=schemas.js.map